import { Button, Combobox, Dialog, notify, Select, StatusPill, TextAreaField, TextField, useConfirm, type ComboOption } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { api, ApiError } from "../../api/client";
import { errorText, previewDoc, splitAddresses, TEMPLATES_KEY, type Draft, type DraftContext, type Rendered, type Sending, type TemplateList } from "./emailApi";

export type ComposeStart = { mailbox: string; kind: "new" | "reply" | "forward"; sourceMessageId?: string | null; to?: string[]; subject?: string; context?: DraftContext };
type Picked = { key: keyof DraftContext; id: string; label: string };
const KIND_TITLE = { new: "New message", reply: "Reply", forward: "Forward" } as const;
const RECORD_KEY = { org: "orgId", quote: "quoteId", shipment: "shipmentId", ticket: "ticketId" } as const;
const RECORD_LABEL = { orgId: "Customer", quoteId: "Quote", shipmentId: "Shipment", ticketId: "Ticket" } as const;

type SearchResp = { groups: { group: string; hits: { id: string; label: string; hint?: string | null }[] }[] };
type QuotesResp = { quotes: { id: string; orgName: string; originPostal: string; destinationPostal: string }[] };
const quoteNo = (id: string) => `Q-${id.replace(/-/g, "").slice(0, 8).toUpperCase()}`;

/**
 * Compose / reply / forward (A.24). Everything here is a DRAFT saved in FlexFreight's database. Sending is a separate,
 * explicit step: only while the owner-approved switch is on, and only after the confirmation dialog shows the mailbox,
 * recipients and subject (and warns about outside recipients).
 */
export function Composer({ start, draft, mailboxes, sending, domains, onClose }: { start?: ComposeStart; draft?: Draft; mailboxes: string[]; sending: Sending; domains: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const kind = draft?.kind ?? start?.kind ?? "new";
  const [id, setId] = useState<string | null>(draft?.id ?? null);
  const [mailbox, setMailbox] = useState(draft?.mailbox ?? start?.mailbox ?? mailboxes[0] ?? "");
  const [to, setTo] = useState((draft?.to ?? start?.to ?? []).join(", "));
  const [cc, setCc] = useState((draft?.cc ?? []).join(", "));
  const [subject, setSubject] = useState(draft?.subject ?? start?.subject ?? "");
  const [body, setBody] = useState(draft?.bodyHtml ?? "");
  const [templateId, setTemplateId] = useState<string | null>(draft?.templateId ?? null);
  const [records, setRecords] = useState<Picked[]>(() =>
    Object.entries(draft?.context ?? start?.context ?? {}).map(([k, v]) => ({ key: k as keyof DraftContext, id: v as string, label: `${RECORD_LABEL[k as keyof DraftContext]} linked` })),
  );
  const [preview, setPreview] = useState<Rendered | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const sourceMessageId = draft?.sourceMessageId ?? start?.sourceMessageId ?? null;
  const context: DraftContext = Object.fromEntries(records.map((r) => [r.key, r.id]));
  const templates = useQuery({ queryKey: TEMPLATES_KEY, queryFn: () => api<TemplateList>("/v1/console/email/templates") });
  const toList = splitAddresses(to);
  const ccList = splitAddresses(cc);
  const outside = [...toList, ...ccList].filter((a) => !domains.some((d) => a.endsWith(`@${d}`)));
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["console", "drafts"] });
  };

  const payload = () => ({ mailbox, kind, sourceMessageId, to: toList, cc: ccList, subject, bodyHtml: body, templateId, context });
  const save = useMutation({
    mutationFn: () => (id ? api<Draft>(`/v1/console/email/drafts/${id}`, { method: "PUT", body: payload() }) : api<Draft>("/v1/console/email/drafts", { method: "POST", body: payload() })),
    onSuccess: (d) => {
      setId(d.id);
      refresh();
    },
  });
  const applyTemplate = useMutation({
    mutationFn: (tid: string) => api<Rendered>("/v1/console/email/templates/preview", { method: "POST", body: { templateId: tid, context } }),
    onSuccess: (r) => {
      if (kind === "new" || !subject.trim()) setSubject(r.subject);
      setBody(r.bodyHtml);
      if (r.missing.length) notify.warning("Some fields are empty", `No value for ${r.missing.map((m) => `{{${m}}}`).join(", ")}. Link the record or fill it in by hand.`);
    },
    onError: (e) => notify.error("Couldn't use that template", errorText(e)),
  });
  const showPreview = useMutation({
    mutationFn: () => api<Rendered>("/v1/console/email/templates/preview", { method: "POST", body: { subject, bodyHtml: body || " ", context } }),
    onSuccess: setPreview,
    onError: (e) => notify.error("Couldn't preview", errorText(e)),
  });
  const sendNow = useMutation({
    mutationFn: (draftId: string) => api<Draft>(`/v1/console/email/drafts/${draftId}/send`, { method: "POST", body: { confirmed: true } }),
    onSuccess: (d) => {
      refresh();
      void qc.invalidateQueries({ queryKey: ["console", "mail"] });
      notify.success("Sent", `From ${d.mailbox} to ${d.to.join(", ")}.`);
      onClose();
    },
    onError: (e) => {
      refresh();
      notify.error("Not sent", errorText(e));
    },
  });
  const discard = useMutation({
    mutationFn: () => api(`/v1/console/email/drafts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      refresh();
      notify.success("Draft discarded");
      onClose();
    },
    onError: (e) => notify.error("Couldn't discard", errorText(e)),
  });

  const loadRecords = useCallback(async (q: string): Promise<ComboOption[]> => {
    const [s, quotes] = await Promise.all([
      api<SearchResp>(`/v1/console/search?${new URLSearchParams({ q })}`).catch(() => ({ groups: [] }) as SearchResp),
      api<QuotesResp>(`/v1/console/quotes?${new URLSearchParams({ q })}`).catch(() => ({ quotes: [] }) as QuotesResp),
    ]);
    const kinds: Record<string, keyof typeof RECORD_KEY> = { Customers: "org", Shipments: "shipment", Tickets: "ticket" };
    const out: ComboOption[] = [];
    for (const g of s.groups) {
      const k = kinds[g.group];
      if (k) for (const h of g.hits) out.push({ value: `${k}:${h.id}`, label: h.label, detail: [g.group.replace(/s$/, ""), h.hint].filter(Boolean).join(" · ") });
    }
    for (const qt of quotes.quotes.slice(0, 5)) out.push({ value: `quote:${qt.id}`, label: `${quoteNo(qt.id)} ${qt.originPostal} to ${qt.destinationPostal}`, detail: `Quote · ${qt.orgName}` });
    return out;
  }, []);
  const pick = (o: ComboOption) => {
    const [k, rid] = o.value.split(":") as [keyof typeof RECORD_KEY, string];
    const key = RECORD_KEY[k];
    setRecords((rs) => [...rs.filter((r) => r.key !== key), { key, id: rid, label: o.label }]);
  };

  async function saveDraft() {
    try {
      await save.mutateAsync();
      notify.success("Draft saved", "Saved in FlexFreight. Nothing was emailed.");
    } catch (e) {
      notify.error("Couldn't save the draft", errorText(e));
    }
  }
  async function trySend() {
    let d: Draft;
    try {
      d = await save.mutateAsync();
    } catch (e) {
      notify.error("Couldn't save the draft", errorText(e));
      return;
    }
    const ok = await confirm({
      title: "Send this email?",
      confirmLabel: "Send email",
      body: (
        <span className="ffa-sendcheck">
          <span className="ffa-sendcheck__row"><strong>From</strong> {d.mailbox}</span>
          <span className="ffa-sendcheck__row"><strong>To</strong> {d.to.join(", ") || "-"}</span>
          {d.cc.length ? <span className="ffa-sendcheck__row"><strong>Cc</strong> {d.cc.join(", ")}</span> : null}
          <span className="ffa-sendcheck__row"><strong>Subject</strong> {d.subject || "(no subject)"}</span>
          {d.externalRecipients.length ? <span className="ffa-sendcheck__warn" role="alert">Outside FlexFreight: {d.externalRecipients.join(", ")}. Check these addresses before sending.</span> : null}
        </span>
      ),
    });
    if (ok) sendNow.mutate(d.id);
  }

  const busy = save.isPending || sendNow.isPending;
  const tplOptions = (templates.data?.templates ?? []).map((t) => ({ value: t.id, label: t.name }));
  return (
    <Dialog
      open
      variant="drawer"
      onClose={onClose}
      title={id ? `${KIND_TITLE[kind]} (draft)` : KIND_TITLE[kind]}
      description="Saved as a draft in FlexFreight. Nothing is emailed unless you send it and confirm."
      footer={
        <>
          {id ? <Button variant="ghost" onClick={async () => { if (await confirm({ title: "Discard this draft?", body: "It is deleted from FlexFreight. Nothing was sent.", confirmLabel: "Discard", danger: true })) discard.mutate(); }}>Discard</Button> : <Button variant="ghost" onClick={onClose}>Cancel</Button>}
          <Button onClick={() => void saveDraft()} loading={save.isPending && !sendNow.isPending}>Save draft</Button>
          <Button variant="primary" disabled={!sending.on || busy || toList.length === 0 || !subject.trim()} loading={sendNow.isPending} onClick={() => void trySend()}>Send…</Button>
        </>
      }
    >
      <div className="ffa-compose">
        <div className="ffa-compose__state">
          <StatusPill tone="warning">Draft</StatusPill>
          <StatusPill tone={sending.on ? "success" : "neutral"}>{sending.label}</StatusPill>
          {draft?.status === "failed" ? <span className="ffa-small" role="alert">Last try wasn't sent: {draft.lastError}</span> : null}
        </div>
        {kind === "new" ? <Select label="From" value={mailbox} onChange={setMailbox} options={mailboxes.map((m) => ({ value: m, label: m }))} /> : <TextField label="From" value={mailbox} readOnly />}
        <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} spellCheck={false} hint="Email addresses, separated by commas" />
        <TextField label="Cc" optional value={cc} onChange={(e) => setCc(e.target.value)} spellCheck={false} />
        {outside.length ? <p className="ffa-compose__warn" role="status">Outside FlexFreight: {outside.join(", ")}</p> : null}
        <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value.replace(/[\r\n]+/g, " "))} maxLength={255} />
        <div className="ffa-compose__fill">
          <Combobox label="Fill variables from a record" placeholder="Customer, BOL, PRO, ticket # or quote ZIP" load={loadRecords} onSelect={pick} emptyText="No customers, shipments, tickets or quotes match." />
          {records.length ? (
            <ul className="ffa-chips" aria-label="Linked records">
              {records.map((r) => (
                <li key={r.key} className="ffa-chip">
                  <span>{RECORD_LABEL[r.key]}: {r.label}</span>
                  <button type="button" className="ffa-chip__x" aria-label={`Remove ${RECORD_LABEL[r.key]}`} onClick={() => setRecords((rs) => rs.filter((x) => x.key !== r.key))}>×</button>
                </li>
              ))}
            </ul>
          ) : null}
          <Select label="Template" placeholder={tplOptions.length ? "Choose a template" : "No templates yet"} disabled={!tplOptions.length} value={templateId} onChange={(v) => { setTemplateId(v); applyTemplate.mutate(v); }} options={tplOptions} />
        </div>
        <TextAreaField label="Message" value={body} onChange={(e) => setBody(e.target.value)} rows={10} maxLength={100000} hint="Plain text, or basic HTML (paragraphs, bold, links, lists). Scripts and styles are removed." />
        <div className="ffa-compose__actions">
          <Button size="sm" onClick={() => showPreview.mutate()} loading={showPreview.isPending}>Preview</Button>
          {!sending.on ? <span className="ffa-small ffa-muted">{sending.label}. You can save drafts; nothing is emailed.</span> : null}
        </div>
        {preview ? (
          <figure className="ffa-compose__preview">
            <figcaption className="ffa-small"><strong>Subject:</strong> {preview.subject || "(no subject)"}{preview.missing.length ? ` · empty: ${preview.missing.join(", ")}` : ""}</figcaption>
            <iframe title="Message preview" sandbox="" srcDoc={previewDoc(preview.bodyHtml)} />
          </figure>
        ) : null}
      </div>
      {confirmDialog}
    </Dialog>
  );
}

export const isApiReason = (e: unknown, reason: string) => e instanceof ApiError && e.reason === reason;
